// La lógica de la home de /sitio (InicioWeb.jsx), sin dibujar nada.
//
// Vive aparte de Web.jsx por lo mismo que `registroCliente.js` y
// `puertaTelegram.js`: este proyecto no monta componentes en las pruebas,
// así que lo que decide qué se muestra tiene que poder probarse solo.

import { filtrarCatalogo, ligaDeEvento, VIVO } from "./catalogoDeportivo";

// Los partidos en curso, sacados del catálogo de GR8.
//
// Antes esto se leía de `/api/live/combined`, que contra staging devuelve
// `{"matches":[],"count":0}`: la pantalla no fallaba, dibujaba una lista
// vacía y por eso nadie lo notó en meses. El catálogo que /sitio ya pedía
// para la pestaña de próximos (`/api/live/all-markets`) trae los mismos
// partidos en curso marcados con `etapa: "Live"` —medido contra staging el
// 2026-10-06: 47 de 204 eventos—, así que la lista en vivo sale de ahí y no
// hay una segunda fuente que mantener.
//
// Quién lee qué: FilaPartido (Web.jsx) usa `h`/`a` y las tarjetas de la
// portada (InicioWeb.jsx) usan `home`/`away` y `liga`. Se mandan las dos
// formas para no tener que tocar ninguna de las dos pantallas.
export function partidosEnVivo(sports, ahora = Date.now()) {
  const salida = [];
  for (const deporte of filtrarCatalogo(sports, { etapa: VIVO }, ahora)) {
    for (const ev of deporte.events) {
      salida.push({
        ...ev,
        home: ev.home ?? ev.h,
        away: ev.away ?? ev.a,
        liga: ligaDeEvento(ev) || deporte.name || "",
        sport_key: ev.sport_key || deporte.key || "",
      });
    }
  }
  return salida;
}

// El combo del día es el primero que haya: primero los que armó la casa
// a mano, después los de la IA. Un combo elegido por una persona le gana
// a uno generado, y así lo hace también la mini-app.
export function elegirCombo(manuales, ia) {
  const todos = [...(manuales?.combos || []), ...(ia?.combos || [])];
  return todos.length ? todos[0] : null;
}

// La cuota del combo es la que GUARDÓ EL SERVIDOR, no un producto que la
// pantalla rearma.
//
// Desde `odd/tasks/cuota-del-combo.md` el precio de un combo se decide una
// sola vez y en un solo lugar —`_odd_del_combo` en casino_api.py, con el mismo
// `_cotizar_mismo_partido` que cobra el boleto— y queda en
// `combos_manuales.odd_total`. Los dos listados que alimentan las pantallas lo
// devuelven: `/api/app/combos-manuales` (casino_api.py:14952) y
// `/api/ai/combos` (:21418).
//
// Multiplicar las cuotas de los picks acá NO daba el mismo número: un combo
// con dos mercados del mismo partido paga el margen de correlación, así que el
// producto pelado queda MÁS ALTO que el precio guardado —medido en la suite
// del servidor: 3.6 contra 3.21—. El jugador leía una cuota que el boleto no
// le iba a pagar.
//
// Esta función ya no es solo de /sitio: `App.jsx` la importa para sus tres
// sitios de display, para que la regla de "cuál es el precio de un combo"
// exista una vez y no cuatro. Su casa definitiva es un módulo de combos junto
// a `combosMismoPartido.js`; queda anotado como hallazgo abierto en el
// documento porque ese archivo no estaba en las superficies autorizadas.
//
// El respaldo —el producto de los picks— no es decorativo y tiene sus propias
// pruebas: hay listados que nombran el campo `odd` en vez de `odd_total`
// (`/api/admin/historial/combos`, casino_api.py:1787) y filas sin precio que
// salen como `float(r["odd_total"] or 0)`, y un 0x en pantalla es peor que un
// producto. Un pick sin cuota no anula el combo: cuenta como 1.
export function cuotaDelCombo(combo) {
  const guardada = Number(combo?.odd_total ?? combo?.odd) || 0;
  if (guardada > 0) return guardada;
  return (combo?.picks || []).reduce((acc, p) => acc * (p.odd || 1), 1);
}

// Del formato del combo al del boleto de /sitio. Los combos manuales no
// siempre traen `event_id`; sin id propio, dos picks de partidos
// distintos chocarían en el boleto, que se llavea por id.
//
// ESTA ES UNA PUERTA QUE VENDE, Y TIRABA LA CLAVE (L5a)
// -----------------------------------------------------
// Desde L4a la fila del combo guarda `market` y `nombre` —el mercado y el
// nombre del resultado TAL COMO LOS EMITE GR8—, pero este cargador no los
// copiaba: escribía la etiqueta en `sel` y clavaba `market:"h2h"`. El pick
// llegaba al carrito de `/sitio` sin nada que `gr8_seleccion.resolver_una`
// pueda encontrar en el índice de claves, se VENDÍA igual —el `sport_key`
// trae el prefijo `gr8_`, así que `_picks_sin_proveedor` no lo traba— y
// fallaba recién al liquidar: el boleto quedaba `otro_proveedor` y lo pagaba
// una persona a mano en el mostrador. Publicar con clave no sirve si el que
// vende la descarta.
//
// `label` y `sel` NO se tocan: son la etiqueta en castellano que el jugador
// lee y la identidad del pick en el boleto. El nombre del emisor viaja al
// lado, en `nombre`, que es lo que `armarBody` (`Web.jsx`) manda como `sel`.
//
// Y `market` ya no cae a `"h2h"`. Un combo guardado antes de L4a no tiene
// mercado, y para el 1X2 del fútbol `"h2h"` es el mercado EQUIVOCADO: GR8 lo
// emite en `h2h_3_way`. Vacío es "no sé de qué mercado salió", que es la
// verdad; `"h2h"` es una afirmación falsa sobre un mercado que el evento
// puede no tener. Esos combos siguen sin liquidar solos, como hasta hoy,
// pero no mienten.
export function picksDelCombo(combo) {
  return (combo?.picks || []).map((p, i) => {
    const id = p.event_id || `${p.h || p.home}-${p.a || p.away}-${i}`;
    return {
      id, event_id: id, label: p.sel, sel: p.sel, nombre: p.nombre || "",
      odd: p.odd, val: p.odd, market: p.market || "",
      h: p.h || p.home, a: p.a || p.away,
      home: p.h || p.home, away: p.a || p.away,
      sport: "", sport_key: p.sport_key || "",
      commence_time: p.commence_time,
    };
  });
}

// Las tres cuotas del 1X2 de un partido en vivo, en el orden en que se
// muestran. Si el partido no tiene ganador apostable devuelve vacío y la
// tarjeta se dibuja sin cuotas en vez de inventar un cero.
export function cuotasDelPartido(m) {
  const h2h = (m?.markets || {}).h2h || {};
  const porMercado = Object.keys(h2h).slice(0, 3)
    .map(nombre => ({ nombre, cuota: h2h[nombre] }));
  if (porMercado.length) return porMercado;
  // El catálogo de GR8 manda el 1X2 en `odds` (L/E/V) y solo a veces
  // también en `markets.h2h`: medido contra staging, 12 de 47 partidos en
  // curso. Sin esta lectura, 35 tarjetas de la portada quedaban sin
  // ninguna cuota teniendo las tres.
  const odds = m?.odds || {};
  return [
    { nombre: m?.home || m?.h || "1", cuota: odds.L },
    { nombre: "Empate", cuota: odds.E },
    { nombre: m?.away || m?.a || "2", cuota: odds.V },
  ].filter(x => x.cuota);
}
